import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, CheckCircle2, Info, Loader2, PlugZap, Save, Share2 } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { formatarDataHora } from '@/lib/utils';
import type { ConexaoEsus } from '@/features/regulacao/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';
import { cn, paraBooleano } from '@/lib/utils';

const esquema = z.object({
  baseUrl: z.string().min(1, 'Informe o endereço da API.'),
  indice: z.string().min(1, 'Informe o índice consultado.'),
  usuario: z.string().min(1, 'Informe o usuário da API.'),
  senha: z.string().optional(),
  timeoutMs: z.coerce.number().int().min(5000).max(120000),
  ativo: z.boolean(),
  agendamentoManualPermitido: z.boolean(),
});

type Formulario = z.infer<typeof esquema>;

interface ResultadoTeste {
  ok: boolean;
  mensagem: string;
  totalDocumentos?: number;
}

export function PaginaEsus() {
  const queryClient = useQueryClient();
  const [resultado, setResultado] = React.useState<ResultadoTeste | null>(null);

  const { data: conexao, isPending } = useQuery({
    queryKey: ['esus-conexao'],
    queryFn: () => api.get<ConexaoEsus>('/esus/conexao'),
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    values: {
      baseUrl: conexao?.baseUrl ?? 'https://regulacao-api.saude.gov.br',
      indice: conexao?.indice ?? 'regulacao-producao',
      usuario: conexao?.usuario ?? '',
      senha: '',
      timeoutMs: conexao?.timeoutMs ?? 30_000,
      ativo: conexao?.ativo ?? false,
      agendamentoManualPermitido: conexao?.agendamentoManualPermitido ?? false,
    },
  });

  const testar = useMutation({
    mutationFn: (dados: Formulario) =>
      api.post<ResultadoTeste>('/esus/conexao/testar', {
        baseUrl: dados.baseUrl,
        indice: dados.indice,
        usuario: dados.usuario,
        senha: dados.senha || undefined,
        timeoutMs: dados.timeoutMs,
      }),
    onSuccess: async (r) => {
      setResultado(r);
      await queryClient.invalidateQueries({ queryKey: ['esus-conexao'] });
    },
    onError: (erro) =>
      setResultado({
        ok: false,
        mensagem: erro instanceof ErroApi ? erro.mensagemUsuario : 'Falha ao testar.',
      }),
  });

  const salvar = useMutation({
    mutationFn: (dados: Formulario) =>
      api.patch<ConexaoEsus>('/esus/conexao', { ...dados, senha: dados.senha || undefined }),
    onSuccess: async () => {
      toast.success('Configuração salva.');
      formulario.setValue('senha', '');
      await queryClient.invalidateQueries({ queryKey: ['esus-conexao'] });
      await queryClient.invalidateQueries({ queryKey: ['esus-situacao'] });
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.'),
  });

  const aoSalvar = formulario.handleSubmit((d) => salvar.mutate(d));
  const aoTestar = formulario.handleSubmit((d) => testar.mutate(d));

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-80" />
        <Esqueleto className="h-96 w-full max-w-2xl" />
      </div>
    );
  }

  const ativo = formulario.watch('ativo');
  const manualPermitido = formulario.watch('agendamentoManualPermitido');

  return (
    <div className="max-w-2xl">
      <CabecalhoPagina
        titulo="Integração com o eSUS Regulação"
        descricao="Consulta somente-leitura à API do DATASUS, usada para confirmar os dados do agendamento."
      />

      <div className="border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto mb-4 flex items-start gap-2 rounded-md border p-3 text-sm">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-medium">Desativada, a Regulação usa um adaptador simulado.</p>
          <p className="mt-0.5">
            O fluxo continua funcionando para treinamento e homologação, mas os dados do agendamento
            são fictícios — e a tela avisa isso ao regulador.
          </p>
        </div>
      </div>

      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo className="flex items-center gap-2">
            <Share2 className="size-4" aria-hidden="true" />
            Dados da conexão
          </CartaoTitulo>
          <CartaoDescricao>
            {conexao?.ultimoTesteEm ? (
              <span className="flex flex-wrap items-center gap-1.5">
                Último teste em {formatarDataHora(conexao.ultimoTesteEm)}
                <Etiqueta tom={conexao.ultimoTesteOk ? 'sucesso' : 'perigo'}>
                  {conexao.ultimoTesteOk ? 'conectado' : 'com problema'}
                </Etiqueta>
              </span>
            ) : (
              'A conexão ainda não foi testada.'
            )}
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo>
          <form onSubmit={(e) => void aoSalvar(e)} noValidate className="flex flex-col gap-4">
            <Campo
              id="esus-url"
              rotulo="Endereço da API"
              obrigatorio
              erro={formulario.formState.errors.baseUrl?.message}
            >
              <Entrada
                placeholder="https://regulacao-api.saude.gov.br"
                {...formulario.register('baseUrl')}
              />
            </Campo>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="esus-indice"
                rotulo="Índice"
                obrigatorio
                ajuda="Normalmente regulacao-producao."
                erro={formulario.formState.errors.indice?.message}
              >
                <Entrada {...formulario.register('indice')} />
              </Campo>

              <Campo
                id="esus-timeout"
                rotulo="Tempo limite (ms)"
                ajuda="Entre 5.000 e 120.000."
                erro={formulario.formState.errors.timeoutMs?.message}
              >
                <Entrada type="number" inputMode="numeric" {...formulario.register('timeoutMs')} />
              </Campo>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                id="esus-usuario"
                rotulo="Usuário"
                obrigatorio
                erro={formulario.formState.errors.usuario?.message}
              >
                <Entrada autoComplete="off" {...formulario.register('usuario')} />
              </Campo>

              <Campo
                id="esus-senha"
                rotulo="Senha"
                ajuda={
                  conexao?.temSenha
                    ? 'Já existe uma senha salva. Deixe em branco para mantê-la.'
                    : 'Nenhuma senha salva ainda.'
                }
              >
                <Entrada
                  type="password"
                  autoComplete="new-password"
                  placeholder={conexao?.temSenha ? '••••••••' : ''}
                  {...formulario.register('senha')}
                />
              </Campo>
            </div>

            <Campo
              id="esus-ativo"
              rotulo="Situação da integração"
              ajuda="Desativada, o agendamento usa dados simulados."
            >
              <Selecao
                {...formulario.register('ativo', {
                  setValueAs: paraBooleano,
                })}
              >
                <option value="false">Desativada (adaptador simulado)</option>
                <option value="true">Ativa (API oficial do DATASUS)</option>
              </Selecao>
            </Campo>

            <Campo
              id="esus-manual"
              rotulo="Agendamento manual"
              ajuda="Vale só quando a consulta falha: com o eSUS respondendo, os dados oficiais continuam obrigatórios."
            >
              <Selecao
                {...formulario.register('agendamentoManualPermitido', {
                  setValueAs: paraBooleano,
                })}
              >
                <option value="false">Bloqueado (esperar a integração voltar)</option>
                <option value="true">Liberado (Regulação pode digitar os dados)</option>
              </Selecao>
            </Campo>

            {manualPermitido && (
              <p className="text-silenciado-texto flex items-start gap-1.5 text-xs">
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                Liberado, a Regulação escolhe local, procedimento e profissional dos cadastros
                quando o eSUS não devolve os dados. O agendamento fica marcado como preenchido
                manualmente — e sem a conferência de identidade do paciente, que depende do retorno
                da integração.
              </p>
            )}

            {ativo && !conexao?.ultimoTesteOk && (
              <p className="text-alerta flex items-start gap-1.5 text-xs" role="status">
                <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                Teste a conexão antes de ativar: sem isso, cada tentativa de agendamento falha na
                mão do regulador.
              </p>
            )}

            {resultado && (
              <div
                role="status"
                className={cn(
                  'flex items-start gap-2 rounded-md border p-3 text-sm',
                  resultado.ok
                    ? 'border-status-agendado-borda bg-status-agendado-fundo text-status-agendado-texto'
                    : 'border-status-devolvido-borda bg-status-devolvido-fundo text-status-devolvido-texto',
                )}
              >
                {resultado.ok ? (
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                ) : (
                  <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                )}
                <span className="min-w-0 break-words">{resultado.mensagem}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Botao
                type="button"
                variante="contorno"
                onClick={() => void aoTestar()}
                disabled={testar.isPending}
              >
                {testar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <PlugZap aria-hidden="true" />
                )}
                {testar.isPending ? 'Testando…' : 'Testar conexão'}
              </Botao>

              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <Save aria-hidden="true" />
                )}
                Salvar
              </Botao>
            </div>
          </form>
        </CartaoConteudo>
      </Cartao>
    </div>
  );
}
